/* eslint-disable react/display-name */
import { Popconfirm, Space, Tooltip, Modal, Form, Input, Button } from "antd";
import { canEditPermission } from "configs/constants";
import { canDeletePermission } from "configs/constants";
import { allowPermission } from "helper/util";
import { selectRole } from "modules/auth/store/authSlice";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { StopOutlined, CheckCircleOutlined } from "@ant-design/icons";

import TableBase from "../../../../../components/table/TableBase";
import {
  deleteClient,
  blockClient,
  unblockClient,
  getClientList
} from "../../store/thunks";

const AdminClientsTable = ({ dataSource, pagination }) => {
  const role = useSelector(selectRole);
  const canEdit = allowPermission(role, canEditPermission);
  const canDelete = allowPermission(role, canDeletePermission);
  const dispatch = useDispatch();
  const history = useHistory();
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [currentClient, setCurrentClient] = useState(null);
  const [blockForm] = Form.useForm();

  const handleDelete = (client) => {
    dispatch(deleteClient(client));
  };

  const handleEdit = (record) => {
    history.push(`/admin/clients/${record.id}`);
  };

  const handleRowClick = (record) => {
    // Navigate to edit page when clicking on the row
    history.push(`/admin/clients/${record.id}`);
  };

  const handleBlockClick = (client) => {
    setCurrentClient(client);
    setShowBlockModal(true);
  };

  const handleUnblockClick = (client) => {
    setCurrentClient(client);
    setShowBlockModal(true);
  };

  const handleBlockSubmit = async (values) => {
    if (!currentClient) return;

    try {
      if (currentClient.isBlocked) {
        await dispatch(
          unblockClient({
            clientId: currentClient.id,
            blockReason: values.blockReason
          })
        );
      } else {
        await dispatch(
          blockClient({
            clientId: currentClient.id,
            blockReason: values.blockReason
          })
        );
      }
      // Refresh the client list to show updated status
      dispatch(getClientList({ page: 1, pageSize: 10 }));
      setShowBlockModal(false);
      blockForm.resetFields();
      setCurrentClient(null);
    } catch (error) {
      console.error("Error updating client block status:", error);
    }
  };

  const handleBlockCancel = () => {
    setShowBlockModal(false);
    blockForm.resetFields();
    setCurrentClient(null);
  };

  const columns = [
    {
      title: "Logo",
      dataIndex: "logoPhoto",
      width: 80,
      render: (logoPhoto, record) => {
        if (!logoPhoto?.url) return null;

        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <img
              src={logoPhoto.url}
              alt={`${record.clientName} logo`}
              style={{
                width: "40px",
                height: "40px",
                objectFit: "contain",
                borderRadius: "4px"
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
        );
      }
    },
    {
      title: "ID",
      dataIndex: "id",
      width: 120,
      render: (value, record) => {
        const displayId = record.customID || value.substr(0, 8).toUpperCase();
        return <p style={{ margin: 0, color: "#1f3998" }}>{displayId}</p>;
      }
    },
    {
      title: "Company Name",
      dataIndex: "clientName",
      width: 150
    },
    {
      title: "Email Address",
      dataIndex: "supportEmail",
      width: 200
    },
    {
      title: "Phone Number",
      dataIndex: "supportPhone",
      width: 200,
      ellipsis: true
    },
    {
      title: "Support Phone Number",
      dataIndex: "supportPhone",
      width: 200,
      ellipsis: true
    },
    {
      title: "Territory",
      dataIndex: ["territory", "name"],
      width: 150
    }
    // {
    //   title: "# of users",
    //   dataIndex: "noOfUsers",
    //   width: 150
    // },
  ];
  if (canEdit || canDelete) {
    columns.push({
      title: "",
      key: "action",
      width: 100,
      fixed: "right",
      // eslint-disable-next-line react/display-name
      render: (value, record) => {
        return (
          <Space size="middle">
            {canEdit && (
              <Tooltip title={record.isBlocked ? "Unblock Client" : "Block Client"}>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    record.isBlocked
                      ? handleUnblockClick(record)
                      : handleBlockClick(record);
                  }}
                >
                  {record.isBlocked ? (
                    <CheckCircleOutlined
                      style={{ color: "#52c41a", fontSize: "16px", cursor: "pointer" }}
                    />
                  ) : (
                    <StopOutlined
                      style={{ color: "#ff4d4f", fontSize: "16px", cursor: "pointer" }}
                    />
                  )}
                </div>
              </Tooltip>
            )}
            {canDelete && (
              <Popconfirm
                title="Are you sure to delete this?"
                onConfirm={(e) => {
                  e?.stopPropagation();
                  handleDelete(record);
                }}
                okText="Yes"
                cancelText="No"
              >
                <img
                  src="/icons/delete.svg"
                  className={`table-status ${value}`}
                  onClick={(e) => e.stopPropagation()}
                />
              </Popconfirm>
            )}
          </Space>
        );
      }
    });
  }

  const getRowClassName = (record) => {
    return record.isBlocked ? "blocked-client-row" : "";
  };

  return (
    <div className="AdminClientsTable">
      <TableBase
        columns={columns}
        data={dataSource}
        pagination={pagination}
        rowKey="id"
        rowClassName={getRowClassName}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: "pointer" }
        })}
      />
      <style>{`
        .blocked-client-row {
          background-color: #ffebee !important;
        }
        .blocked-client-row:hover {
          background-color: #ffcdd2 !important;
        }
        .ant-table-tbody .blocked-client-row:hover > td {
          background-color: #ffcdd2 !important;
        }
      `}</style>

      {/* Block/Unblock Reason Modal */}
      <Modal
        title={`${currentClient?.isBlocked ? "Unblock" : "Block"} 
          Client - Reason Required`}
        visible={showBlockModal}
        onCancel={handleBlockCancel}
        width={600}
        footer={null}
        getContainer={() => document.body}
      >
        <Form form={blockForm} onFinish={handleBlockSubmit} layout="vertical">
          <Form.Item
            name="blockReason"
            label={`Please explain why you are ${
              currentClient?.isBlocked ? "unblocking" : "blocking"
            } this client:`}
            rules={[
              {
                required: !currentClient?.isBlocked,
                message: "Block reason is required!"
              }
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder={`Enter detailed reason for ${
                currentClient?.isBlocked ? "unblocking" : "blocking"
              } this client (e.g., payment issues, policy violations, etc.)`}
              maxLength={500}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {currentClient?.isBlocked ? "Unblock Client" : "Block Client"}
              </Button>
              <Button onClick={handleBlockCancel}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
export default AdminClientsTable;
